import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import type { Testimonial } from '@/lib/types';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-1 text-accent-500" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={i < rating ? 'ri-star-fill' : 'ri-star-line'} aria-hidden="true" />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const { rows: testimonials, loading, error, reload } = useCollection<Testimonial>('testimonials', 'created_at', false);
  const [active, setActive] = useState(0);
  const total = testimonials.length;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (total <= 1) return;
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 5000);
  };

  useEffect(() => {
    startTimer();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const goTo = (idx: number) => {
    setActive(idx);
    startTimer();
  };

  const prev = () => { goTo((active - 1 + total) % total); };
  const next = () => { goTo((active + 1) % total); };

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
            Testimonials
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
            What Our Members Say
          </h2>
          <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
          <p className="text-foreground-500 mt-5 text-base max-w-lg mx-auto">
            Real stories from real people whose lives have been touched by the TGMCI community.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-foreground-400 py-10">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading testimonials...
          </p>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-600 text-sm mb-3">Could not load testimonials.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : total === 0 ? (
          <p className="text-center text-foreground-400 py-10">No testimonials yet.</p>
        ) : (
          <>
            {/* Desktop: 3 cards */}
            <div className="hidden md:grid grid-cols-3 gap-6 mb-10">
              {[testimonials[(active + total - 1) % total], testimonials[active], testimonials[(active + 1) % total]].map((t, idx) => (
                <article
                  key={`${t.id}-${idx}`}
                  className={`p-7 rounded-xl border transition-all duration-500 ${
                    idx === 1
                      ? 'border-primary-200 bg-primary-600 text-white scale-105 -translate-y-2'
                      : 'border-background-200 bg-background-50 text-foreground-800'
                  }`}
                >
                  <StarRating rating={t.rating} />
                  <blockquote className={`font-heading text-lg italic leading-relaxed mt-4 mb-5 ${idx === 1 ? 'text-white' : 'text-foreground-700'}`}>
                    &ldquo;{t.text}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full font-heading font-bold text-sm ${idx === 1 ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'}`}>
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className={`font-semibold text-sm ${idx === 1 ? 'text-white' : 'text-foreground-800'}`}>{t.name}</div>
                      <div className={`text-xs ${idx === 1 ? 'text-white/70' : 'text-foreground-500'}`}>{t.role}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Mobile: single card */}
            <div className="md:hidden mb-8">
              <article className="p-7 rounded-xl border border-primary-200 bg-primary-600 text-white">
                <StarRating rating={testimonials[active].rating} />
                <blockquote className="font-heading text-lg italic leading-relaxed mt-4 mb-5 text-white">
                  &ldquo;{testimonials[active].text}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white font-heading font-bold text-sm">
                    {testimonials[active].name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-white">{testimonials[active].name}</div>
                    <div className="text-xs text-white/70">{testimonials[active].role}</div>
                  </div>
                </div>
              </article>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous testimonial"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" aria-hidden="true" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => goTo(idx)}
                    aria-label={`Go to testimonial ${idx + 1}`}
                    className={`cursor-pointer transition-all rounded-full ${
                      idx === active ? 'bg-primary-600 w-6 h-2' : 'bg-background-300 w-2 h-2 hover:bg-primary-400'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={next}
                aria-label="Next testimonial"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </button>
            </div>
          </>
        )}

        <div className="text-center mt-8">
          <Link to="/testimonials" className="btn-outline text-sm">
            Read All Testimonials
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}